import { SimplifiedSKU } from './skuDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { PLATFORM_ORG_ID } from 'domains/orgDomain';
import { PageParam, CodeName } from './appDomain';
export interface Markup {
  active: boolean;
  description: string;
  elementType: string;
  endDt: string;
  id: number;
  isFree: boolean;
  isRange: boolean;
  markupRanges: MarkupRange[];
  orgSkuFilters: OrgSkuFilter[];
  orgShippingFilters: OrgShippingFilter[];
  orgId: number;
  startDt: string;
  type: string;
  version: number;
}

export interface MarkupRange {
  hasHighCost: boolean;
  highCost: number;
  id: number;
  lowCost: number;
  markupPct: number | null;
  skuReplace: ReplaceTypeEnum;
}

export interface OrgSkuFilter {
  orgId: number;
  skuIds: number[];
}

export interface OrgShippingFilter {
  orgId: number;
  carrierTypes: CarrierTypeEnum[];
}

export enum MarkupTypeEnum {
  ELEMENT = 'ELEMENT',
  ELEMENT_OVERRIDE = 'ELEMENT_OVERRIDE',
  GLOBAL = 'GLOBAL',
  GLOBAL_OVERRIDE = 'GLOBAL_OVERRIDE',
  ORG_SKU = 'ORG_SKU',
  OUTSOURCE_PRINTING = 'OUTSOURCE_PRINTING',
  OUTSOURCE_SHIPPING = 'OUTSOURCE_SHIPPING',
  REVSHOP = 'REVSHOP',
  REVSHOP_OVERRIDE = 'REVSHOP_OVERRIDE',
  REVSHOP_SKU = 'REVSHOP_SKU',
  REVSHOP_SHIPPING = 'REVSHOP_SHIPPING',
}

export enum ElementTypeEnum {
  PAPER = 'PAPER',
  PRESS = 'PRESS',
  SVC = 'SVC',
  SVC_MATERIAL = 'SVC_MATERIAL',
  SHIPPING = 'SHIPPING',
  PARCEL = 'PARCEL',
  NA = 'NA',
}
export const ElementTypeSelectOptions: CodeName[] = [
  { code: ElementTypeEnum.PAPER, name: 'Paper' },
  { code: ElementTypeEnum.PRESS, name: 'Press' },
  { code: ElementTypeEnum.SVC, name: 'Service' },
  { code: ElementTypeEnum.SVC_MATERIAL, name: 'Service Material' },
  { code: ElementTypeEnum.SHIPPING, name: 'Shipping' },
  { code: ElementTypeEnum.PARCEL, name: 'Parcel' },
];
export enum ReplaceTypeEnum {
  NO_ADDITIONAL = 'NO_ADDITIONAL',
  REPLACE_GLOBAL = 'REPLACE_GLOBAL',
  REPLACE_GLOBAL_AND_ELEMENT = 'REPLACE_GLOBAL_AND_ELEMENT',
  NA = 'NA',
}

export const EMPTY_MARKUP: Partial<Markup> = {
  active: true,
  elementType: '',
  endDt: '',
  isRange: false,
  orgSkuFilters: [],
  startDt: '',
  type: '',
};

export const EMPTY_MARKUP_RANGE: Partial<MarkupRange> = {
  hasHighCost: false,
  highCost: 0,
  lowCost: 0,
  markupPct: null,
  skuReplace: ReplaceTypeEnum.NA,
};

export const EMPTY_ELEMENT_OVERRIDE_MARKUP: PartialMarkup = {
  active: true,
  description: '',
  elementType: ElementTypeEnum.PAPER,
  isRange: false,
  markupPct: 0,
  markupRanges: [EMPTY_MARKUP_RANGE],
  type: MarkupTypeEnum.ELEMENT_OVERRIDE,
};

export const EMPTY_GLOBAL_OVERRIDE_MARKUP: PartialMarkup = {
  active: true,
  description: '',
  elementType: ElementTypeEnum.NA,
  isRange: false,
  markupPct: 0,
  markupRanges: [EMPTY_MARKUP_RANGE],
  type: MarkupTypeEnum.GLOBAL_OVERRIDE,
};

export type PartialMarkup = Partial<
  Omit<Markup, 'markupRanges'> & { markupRanges: Partial<MarkupRange>[] }
> & { markupPct?: number };

export function buildCreatePaperMarkupParam(
  markupPct: number | null
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.PAPER,
    type: MarkupTypeEnum.ELEMENT,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}
export function buildCreatePressMarkupParam(
  markupPct: number | null
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.PRESS,
    type: MarkupTypeEnum.ELEMENT,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}
export function buildCreateSvcMarkupParam(
  markupPct: number | null
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.SVC,
    type: MarkupTypeEnum.ELEMENT,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}
export function buildCreateSvcmatMarkupParam(
  markupPct: number | null
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.SVC_MATERIAL,
    type: MarkupTypeEnum.ELEMENT,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}
export function buildCreateParcelMarkupParam(
  markupPct: number | null
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.PARCEL,
    type: MarkupTypeEnum.ELEMENT,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}
export function buildCreateShippingMarkupParam(
  markupPct: number | null
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.SHIPPING,
    type: MarkupTypeEnum.ELEMENT,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}

export interface ListMarkupParam {
  page: PageParam;
  query?: QueryMarkupParam;
}

export interface QueryMarkupParam {
  type: MarkupTypeEnum;
  elementTypeEnum?: ElementTypeEnum;
  active?: boolean;
}

export function buildMarkupParam(
  markupPct: number | null,
  type:
    | MarkupTypeEnum.GLOBAL
    | MarkupTypeEnum.OUTSOURCE_PRINTING
    | MarkupTypeEnum.OUTSOURCE_SHIPPING
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.NA,
    type,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}
export function buildCreateOrgSkuMarkupParam(orgId: number): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    description: 'NO DESCRIPTION',
    elementType: ElementTypeEnum.NA,
    type: MarkupTypeEnum.ORG_SKU,
    isRange: false,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        skuReplace: ReplaceTypeEnum.REPLACE_GLOBAL,
        markupPct: 10,
      },
    ],
    orgId,
    orgSkuFilters: [
      { orgId: PLATFORM_ORG_ID, skuIds: [] },
      { orgId, skuIds: [] },
    ],
  };
}

export interface SkuMarkupTableRecord extends Markup {
  orgCount: number;
  skuCount: number;
  tiers: number;
}

export function buildCreateRevshopMarkupParam(
  markupPct: number | null
): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    elementType: ElementTypeEnum.NA,
    type: MarkupTypeEnum.REVSHOP,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct,
      },
    ],
  };
}

export const EMPTY_REVSHOP_OVERRIDE_MARKUP: PartialMarkup = {
  active: true,
  description: '',
  elementType: ElementTypeEnum.NA,
  isRange: false,
  markupPct: 0,
  markupRanges: [EMPTY_MARKUP_RANGE],
  type: MarkupTypeEnum.REVSHOP_OVERRIDE,
};

export function buildCreateRevshopSkuMarkupParam(): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    description: 'NO DESCRIPTION',
    elementType: ElementTypeEnum.NA,
    type: MarkupTypeEnum.REVSHOP_SKU,
    isRange: false,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        skuReplace: ReplaceTypeEnum.NA,
        markupPct: 10,
      },
    ],
    orgId: PLATFORM_ORG_ID,
    orgSkuFilters: [{ orgId: PLATFORM_ORG_ID, skuIds: [] }],
  };
}

export function buildCreateRevshopShippingMarkupParam(): PartialMarkup {
  return {
    ...EMPTY_MARKUP,
    description: 'NO DESCRIPTION',
    elementType: ElementTypeEnum.NA,
    type: MarkupTypeEnum.REVSHOP_SHIPPING,
    isFree: false,
    isRange: false,
    markupRanges: [
      {
        ...EMPTY_MARKUP_RANGE,
        markupPct: 10,
      },
    ],
    orgId: PLATFORM_ORG_ID,
    orgShippingFilters: [],
  };
}

export interface ShippingMarkupTableRecord extends Markup {
  orgCount: number;
  carrierCount: number;
}

export interface MarkupSkus {
  appliedSkus: MarkupAppliedSkus[];
  notAppliedSkus: SimplifiedSKU[];
}

export interface MarkupAppliedSkus {
  markupId: number;
  skus: SimplifiedSKU[];
}

export interface RevshopOrgMarkupDTO {
  id: number;
  orgId: number;
  appliedOrgId: number;
  appliedOrgName: string;
  markupPct: number;
  marketplaceNetworks: RevshopOrgMarkupDTO[];
  version: number;
}

export interface RevshopOrgMarkupParams {
  id?: number;
  appliedOrgId: number;
  markupPct?: number;
  version?: number;
  marketplaceNetworks?: RevshopOrgMarkupParams[];
}
